import React, { useState, useEffect, useRef, useMemo } from 'react';
import './DesktopAddToGroupModal.css';

function DesktopAddToGroupModal({
  isOpen,
  onClose,
  group,
  allPeople, // All donors
  onSelectExisting, // (personName, groupId) => void
  onAddNew // () => void - opens the Add Person modal
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [isAdding, setIsAdding] = useState(null); // person name being added
  const searchInputRef = useRef(null);

  // Focus search when modal opens
  useEffect(() => {
    if (isOpen) {
      setSearchQuery('');
      setIsAdding(null);
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Filter people - show those NOT already in this group
  const availablePeople = useMemo(() => {
    if (!allPeople || !group) return [];

    const peopleList = Object.entries(allPeople).map(([name, data]) => ({
      name,
      ...data
    }));

    // Filter out people already in this group
    const targetId = String(group.id);
    const notInGroup = peopleList.filter(p => {
      // Check if they have group_ids array with items
      if (p.group_ids && Array.isArray(p.group_ids) && p.group_ids.length > 0) {
        return !p.group_ids.some(id => String(id) === targetId);
      }
      // Fall back to single group_id
      return String(p.groupId) !== targetId && String(p.group_id) !== targetId;
    });

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      return notInGroup.filter(p =>
        p.name.toLowerCase().includes(query) ||
        (p.narrative && p.narrative.toLowerCase().includes(query))
      );
    }

    return notInGroup;
  }, [allPeople, group, searchQuery]);

  const handleSelectPerson = async (person) => {
    setIsAdding(person.name);
    try {
      await onSelectExisting(person.name, group.id);
      onClose();
    } catch (error) {
      console.error('Error adding person to group:', error);
      setIsAdding(null);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const getInitials = (name) => {
    return name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
  };

  const getPersonColor = (name) => {
    const colors = ['#a78bfa', '#4ade80', '#fbbf24', '#60a5fa', '#f472b6', '#ef4444'];
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  };

  const getGroupInfo = (person) => {
    // Check for multi-group membership
    const groupIds = person.group_ids && Array.isArray(person.group_ids)
      ? person.group_ids
      : (person.groupId || person.group_id ? [person.groupId || person.group_id] : []);

    if (groupIds.length === 0) {
      return 'No group';
    } else if (groupIds.length === 1) {
      return 'In 1 group';
    } else {
      return `In ${groupIds.length} groups`;
    }
  };

  if (!isOpen || !group) return null;

  return (
    <div className="add-to-group-modal-backdrop" onClick={handleBackdropClick}>
      <div className="add-to-group-modal">
        <div className="add-to-group-modal-header">
          <svg className="add-to-group-modal-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
          </svg>
          <div className="add-to-group-modal-title">
            <h3>Add to {group.name}</h3>
            <span>Select an existing person or add someone new</span>
          </div>
          <button className="add-to-group-modal-close" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="add-to-group-modal-search">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21l-4.35-4.35" />
          </svg>
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Search people..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <div className="add-to-group-modal-content">
          {/* Add New Person option */}
          <button
            className="add-to-group-new-person"
            onClick={() => {
              onClose();
              onAddNew();
            }}
          >
            <div className="add-to-group-new-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 5v14M5 12h14"/>
              </svg>
            </div>
            <div className="add-to-group-new-text">
              <span className="add-to-group-new-title">Add New Person</span>
              <span className="add-to-group-new-subtitle">Create a new contact and add to this group</span>
            </div>
            <svg className="add-to-group-new-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 18l6-6-6-6"/>
            </svg>
          </button>

          {/* Existing People List */}
          {availablePeople.length > 0 && (
            <div className="add-to-group-people-section">
              <div className="add-to-group-section-label">
                Existing People ({availablePeople.length})
              </div>
              <div className="add-to-group-people-list">
                {availablePeople.slice(0, 20).map((person) => (
                  <button
                    key={person.name}
                    className={`add-to-group-person ${isAdding === person.name ? 'adding' : ''}`}
                    onClick={() => handleSelectPerson(person)}
                    disabled={isAdding !== null}
                  >
                    <div
                      className="add-to-group-person-avatar"
                      style={{ borderColor: getPersonColor(person.name), color: getPersonColor(person.name) }}
                    >
                      {getInitials(person.name)}
                    </div>
                    <div className="add-to-group-person-info">
                      <span className="add-to-group-person-name">{person.name}</span>
                      <span className="add-to-group-person-meta">{getGroupInfo(person)}</span>
                    </div>
                    {isAdding === person.name ? (
                      <span className="add-to-group-adding">Adding...</span>
                    ) : (
                      <svg className="add-to-group-person-add" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 5v14M5 12h14"/>
                      </svg>
                    )}
                  </button>
                ))}
              </div>
              {availablePeople.length > 20 && (
                <div className="add-to-group-more">
                  {availablePeople.length - 20} more people match your search
                </div>
              )}
            </div>
          )}

          {availablePeople.length === 0 && searchQuery && (
            <div className="add-to-group-empty">
              No people found matching "{searchQuery}"
            </div>
          )}

          {availablePeople.length === 0 && !searchQuery && (
            <div className="add-to-group-empty">
              Everyone is already in this group
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default DesktopAddToGroupModal;
